import React, { useState } from 'react';
import { API_BASE_URL, logger, apiFetch } from '../config';

/**
 * SubscriptionPaywall - Shown when user's trial has expired or subscription is inactive.
 * Provides a "Subscribe Now" button that creates a Stripe checkout session and redirects.
 */
function SubscriptionPaywall({ username, subscriptionStatus, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          logger.info('Redirecting to Stripe checkout:', data.checkout_url);
          window.location.href = data.checkout_url;
        } else {
          setError('Could not create checkout session. Please try again.');
        }
      } else {
        setError('Server error. Please try again later.');
      }
    } catch (err) {
      logger.error('Error creating checkout:', err);
      setError('Could not connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMessage = () => {
    switch (subscriptionStatus) {
      case 'trial_expired':
        return 'Your 7-day free trial has expired.';
      case 'canceled':
        return 'Your subscription has been canceled.';
      case 'past_due':
        return 'Your payment failed. Please update your payment method.';
      default:
        return 'A subscription is required to access PySage.';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    }}>

      {/* Redirect overlay */}
      {loading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.92)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 20,
        }}>
          <div style={{
            width: 52, height: 52,
            border: '5px solid rgba(255,255,255,0.15)',
            borderTop: '5px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 0.9s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#e2e8f0', fontSize: 17, fontWeight: 600, margin: 0 }}>
            Redirecting to payment…
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
            You'll be taken to Stripe's secure checkout.
          </p>
        </div>
      )}
      <div style={{
        background: 'rgba(30, 41, 59, 0.95)',
        borderRadius: '20px',
        padding: '48px 40px',
        maxWidth: '480px',
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        border: '1px solid rgba(234, 179, 8, 0.45)',
      }}>
        {/* Logo */}
        <img 
          src="/PySage.png" 
          alt="PySage Logo" 
          style={{ height: '80px', marginBottom: '20px' }} 
        />

        {/* Lock icon */}
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>

        {/* Message */}
        <h2 style={{
          color: '#fff',
          fontSize: '22px',
          fontWeight: '700',
          marginBottom: '12px',
        }}>
          Subscription Required
        </h2>
        <p style={{
          color: '#b0bec5',
          fontSize: '16px',
          marginBottom: '8px',
          lineHeight: '1.5',
        }}>
          {getMessage()}
        </p>
        <p style={{
          color: '#8a9bb0',
          fontSize: '14px',
          marginBottom: '32px',
        }}>
          Subscribe to continue using PySage Tutor for the semester.
        </p>

        {/* Subscribe button */}
        <button
          onClick={handleSubscribe}
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: '700',
            color: '#fff',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            border: 'none',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
            marginBottom: '16px',
          }}
          onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
          onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)')}
        >
          {loading ? '⏳ Redirecting to payment...' : '💳 Subscribe Now'}
        </button>

        {/* Error message */}
        {error && (
          <p style={{ color: '#f87171', fontSize: '14px', marginBottom: '16px' }}>{error}</p>
        )}

        {/* Logout link */}
        <button
          onClick={onLogout}
          style={{
            background: 'none',
            border: 'none',
            color: '#fefce8',
            fontSize: '14px',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '8px',
          }}
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );
}

export default SubscriptionPaywall;
